import { FC, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledAutocomplete from "../../components/controlledComponents/ControlledAutocomplete";
import {
  ACADEMIC_ASSESSMENT_STATUS_VALUES,
  ACADEMIC_ASSESSMENT_TYPE_VALUES,
  TAcademicAssessmentWireStatus,
  TAcademicAssessmentWireType,
} from "../../../apis/assessment/assessment.interfaces";
import {
  useAcademicAssessmentByIdHook,
  useCreateAcademicAssessmentHook,
  useUpdateAcademicAssessmentHook,
} from "../../../hooks/useAssessmentHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import {
  useClassHook,
  useClassSubjectsHook,
  useSubjectsHook,
} from "../../../hooks/useAcademicHook";
import {
  academicAssessmentWireRefId,
  academicAssessmentWireRefName,
  wireAssessmentStatusToFormValue,
  wireAssessmentTypeToFormValue,
} from "./assessment.helpers";
import type {
  TClassSubject,
  TSubject,
} from "../../../apis/academic/academic.interfaces";

const TYPE_ENUM = ACADEMIC_ASSESSMENT_TYPE_VALUES as unknown as [
  TAcademicAssessmentWireType,
  ...TAcademicAssessmentWireType[],
];

const STATUS_ENUM = ACADEMIC_ASSESSMENT_STATUS_VALUES as unknown as [
  TAcademicAssessmentWireStatus,
  ...TAcademicAssessmentWireStatus[],
];

const academicAssessmentSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(200, "Name must not exceed 200 characters"),
  assessmentType: z.enum(TYPE_ENUM, {
    errorMap: () => ({ message: "Please select an assessment type" }),
  }),
  maxScore: z.coerce
    .number({ invalid_type_error: "Max score must be a number" })
    .positive("Max score must be greater than 0"),
  classId: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .refine((val) => val && val.id, {
      message: "Please select a class",
    }),
  courseId: z.object({
    id: z.string(),
    label: z.string(),
  }),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  status: z.enum(STATUS_ENUM, {
    errorMap: () => ({ message: "Please select a status" }),
  }),
  academicYear: z
    .string()
    .max(32, "Academic year is too long")
    .optional()
    .or(z.literal("")),
  instructions: z
    .string()
    .max(2000, "Instructions must not exceed 2000 characters")
    .optional()
    .or(z.literal("")),
});

type AcademicAssessmentFormData = z.infer<typeof academicAssessmentSchema>;

const defaultFormValues = (): AcademicAssessmentFormData => ({
  name: "",
  assessmentType: "exam",
  maxScore: 100,
  classId: { id: "", label: "" },
  courseId: { id: "", label: "" },
  description: "",
  status: "draft",
  academicYear: "",
  instructions: "",
});

const TYPE_LABELS: Record<TAcademicAssessmentWireType, string> = {
  exam: "Exam",
  test: "Test",
  assignment: "Assignment",
  project: "Project",
  quiz: "Quiz",
  mid_term: "Mid-term",
  final: "Final",
  unit_test: "Unit test",
  practical: "Practical",
};

const STATUS_LABELS: Record<TAcademicAssessmentWireStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  graded: "Graded",
  cancelled: "Cancelled",
};

function subjectAutocompleteLabelFor(
  subjects: TSubject[] | undefined,
  classSubjects: TClassSubject[] | undefined,
  subjectId: string,
  fallbackRef?: unknown,
): string {
  if (!subjectId) return "";
  const linked = classSubjects?.find((cs) => cs.subjectId === subjectId);
  if (linked?.subjectName) return linked.subjectName;
  const cat = subjects?.find((s) => s.id === subjectId);
  if (cat) return cat.name;
  return academicAssessmentWireRefName(fallbackRef) ?? "";
}

interface ManageAcademicAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, dialog loads this assessment and saves via PATCH/update. */
  editAssessmentId?: string | null;
}

const ManageAcademicAssessmentDialog: FC<
  ManageAcademicAssessmentDialogProps
> = ({ open, onClose, editAssessmentId = null }) => {
  const { selectedInstitute } = useInstituteContext();
  const createMutation = useCreateAcademicAssessmentHook();
  const updateMutation = useUpdateAcademicAssessmentHook();
  const { data: classes } = useClassHook();
  const { data: subjects } = useSubjectsHook();

  const editingId = editAssessmentId ?? null;
  const isEdit = !!editingId;

  const { data: detail, isPending: editDetailPending } =
    useAcademicAssessmentByIdHook(editingId ?? undefined, {
      enabled: open && isEdit,
    });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AcademicAssessmentFormData>({
    resolver: zodResolver(academicAssessmentSchema),
    defaultValues: defaultFormValues(),
  });

  const watchedClassId = watch("classId");
  const { data: classSubjects } = useClassSubjectsHook(watchedClassId?.id);

  const classOptions = useMemo(
    () =>
      classes?.map((cls) => ({
        id: cls.id,
        label: cls.name,
      })) || [],
    [classes],
  );

  const courseOptions = useMemo(() => {
    if (!watchedClassId?.id || !classSubjects?.length) return [];
    return classSubjects.map((cs) => ({
      id: cs.subjectId,
      label: cs.subjectName ?? cs.subjectCode ?? cs.subjectId,
    }));
  }, [classSubjects, watchedClassId?.id]);

  const typeOptions = useMemo(
    () =>
      [...ACADEMIC_ASSESSMENT_TYPE_VALUES].map((key) => ({
        key,
        value: TYPE_LABELS[key],
      })),
    [],
  );

  const statusOptions = useMemo(
    () =>
      [...ACADEMIC_ASSESSMENT_STATUS_VALUES].map((key) => ({
        key,
        value: STATUS_LABELS[key],
      })),
    [],
  );

  const hasClasses = classOptions.length > 0;
  const prevClassIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    if (isEdit) return;
    reset(defaultFormValues());
    prevClassIdRef.current = undefined;
  }, [open, isEdit, reset]);

  useEffect(() => {
    if (!open || !isEdit || !editingId) return;
    if (!detail || editDetailPending || detail.id !== editingId) return;

    const classIdResolved = academicAssessmentWireRefId(detail.class);
    const courseIdResolved = academicAssessmentWireRefId(detail.course);

    reset({
      name: detail.name,
      assessmentType: wireAssessmentTypeToFormValue(detail.assessmentType),
      maxScore: detail.maxScore,
      classId: {
        id: classIdResolved ?? "",
        label:
          academicAssessmentWireRefName(detail.class) ??
          classes?.find((c) => c.id === classIdResolved)?.name ??
          "",
      },
      courseId: {
        id: courseIdResolved ?? "",
        label: subjectAutocompleteLabelFor(
          subjects,
          classSubjects,
          courseIdResolved ?? "",
          detail.course,
        ),
      },
      description: detail.description ?? "",
      status: wireAssessmentStatusToFormValue(detail.status),
      academicYear: detail.academicYear ?? "",
      instructions: detail.instructions ?? "",
    });
    prevClassIdRef.current = classIdResolved;
  }, [
    open,
    isEdit,
    editingId,
    detail,
    editDetailPending,
    classes,
    subjects,
    classSubjects,
    reset,
  ]);

  useEffect(() => {
    const id = watchedClassId?.id;
    if (prevClassIdRef.current !== undefined && prevClassIdRef.current !== id) {
      setValue("courseId", { id: "", label: "" });
    }
    prevClassIdRef.current = id;
  }, [watchedClassId?.id, setValue]);

  const onInvalid = (validationErrors: typeof errors) => {
    const firstMessage = Object.values(validationErrors)
      .map((e) => (e as { message?: string } | undefined)?.message)
      .find((m): m is string => !!m);
    logger.show_error(
      firstMessage ?? "Please fix the highlighted fields before saving.",
    );
  };

  const onSubmit = async (data: AcademicAssessmentFormData) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before continuing.",
      );
      return;
    }

    if (!hasClasses) {
      logger.show_error(
        "Create classes and subjects in Academics before adding assessments.",
      );
      return;
    }

    try {
      if (isEdit && editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          request: {
            name: data.name,
            assessmentType: data.assessmentType,
            maxScore: data.maxScore,
            status: data.status,
            classId: data.classId.id,
            courseId: data.courseId.id || undefined,
            description: data.description || undefined,
            academicYear: data.academicYear || undefined,
            instructions: data.instructions || undefined,
          },
        });
        logger.show_success("Assessment updated successfully!");
      } else {
        await createMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          name: data.name,
          assessmentType: data.assessmentType,
          maxScore: data.maxScore,
          status: data.status,
          classId: data.classId.id,
          courseId: data.courseId.id || undefined,
          description: data.description || undefined,
          academicYear: data.academicYear || undefined,
          instructions: data.instructions || undefined,
        });
        logger.show_success("Assessment created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : isEdit
            ? "Failed to update assessment. Please try again."
            : "Failed to create assessment. Please try again.",
      );
    }
  };

  const isMutationPending =
    createMutation.isPending || updateMutation.isPending;
  const isLoading =
    isSubmitting || isMutationPending || (open && isEdit && editDetailPending);
  const isError =
    (isEdit ? updateMutation.isError : createMutation.isError) && !editDetailPending;
  const errorMessage =
    (isEdit ? updateMutation.error : createMutation.error)?.message ?? null;

  if (!selectedInstitute) {
    return null;
  }

  const showEditSpinner =
    open &&
    isEdit &&
    (editDetailPending || !detail?.id || detail.id !== editingId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-academic-assessment-dialog"
    >
      <DialogTitle data-testid="manage-academic-assessment-dialog-title">
        {isEdit ? "Edit assessment" : "Create assessment"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        data-testid="academic-assessment-form"
      >
        <DialogContent data-testid="academic-assessment-form-content">
          {showEditSpinner ? (
            <Box
              display="flex"
              justifyContent="center"
              py={6}
              data-testid="academic-assessment-edit-loading"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {isError && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  data-testid="academic-assessment-form-error"
                >
                  {errorMessage ??
                    (isEdit
                      ? "Failed to update assessment. Please try again."
                      : "Failed to create assessment. Please try again.")}
                </Alert>
              )}

              {!hasClasses && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  No classes are available. Add classes under Academics first.
                </Alert>
              )}

              {hasClasses &&
                !!watchedClassId?.id &&
                classSubjects &&
                classSubjects.length === 0 && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    No subjects are assigned to this class. Wire subjects under
                    Academics → Classes → class hub before linking an assessment.
                  </Alert>
                )}

              <Grid
                container
                spacing={3}
                sx={{ mt: 1 }}
                data-testid="academic-assessment-form-grid"
              >
                <ControlledTextField
                  control={control}
                  name="name"
                  id="academic-assessment-name"
                  label="Assessment name"
                  required
                  errorMessage={errors.name?.message}
                  inputProps={{ "data-testid": "academic-assessment-name-input" }}
                />

                <ControlledSelectField
                  control={control}
                  name="assessmentType"
                  id="academic-assessment-type"
                  label="Assessment type"
                  itemList={typeOptions}
                  isRequired
                  errorMessage={errors.assessmentType?.message}
                />

                <ControlledTextField
                  control={control}
                  name="maxScore"
                  id="academic-assessment-max-score"
                  label="Max score"
                  type="number"
                  required
                  errorMessage={errors.maxScore?.message}
                  inputProps={{
                    "data-testid": "academic-assessment-max-score-input",
                  }}
                />

                <ControlledAutocomplete
                  control={control}
                  name="classId"
                  id="academic-assessment-class"
                  label="Class"
                  dataOptions={classOptions}
                  errorMessage={errors.classId?.message}
                  dataTestId="academic-assessment-class-input"
                  disabled={!hasClasses}
                />

                <ControlledAutocomplete
                  control={control}
                  name="courseId"
                  id="academic-assessment-subject"
                  label="Subject (optional)"
                  dataOptions={courseOptions}
                  errorMessage={errors.courseId?.message}
                  dataTestId="academic-assessment-subject-input"
                  disabled={
                    !watchedClassId?.id ||
                    !classSubjects ||
                    classSubjects.length === 0
                  }
                />

                <ControlledSelectField
                  control={control}
                  name="status"
                  id="academic-assessment-status"
                  label="Status"
                  itemList={statusOptions}
                  isRequired
                  errorMessage={errors.status?.message}
                />

                <ControlledTextField
                  control={control}
                  name="academicYear"
                  id="academic-assessment-year"
                  label="Academic year (optional)"
                  errorMessage={errors.academicYear?.message}
                  inputProps={{ "data-testid": "academic-assessment-year-input" }}
                />

                <ControlledTextField
                  control={control}
                  name="description"
                  id="academic-assessment-description"
                  label="Description"
                  multiline
                  rows={2}
                  errorMessage={errors.description?.message}
                  inputProps={{
                    "data-testid": "academic-assessment-description-input",
                  }}
                />

                <ControlledTextField
                  control={control}
                  name="instructions"
                  id="academic-assessment-instructions"
                  label="Instructions (optional)"
                  multiline
                  rows={2}
                  errorMessage={errors.instructions?.message}
                  inputProps={{
                    "data-testid": "academic-assessment-instructions-input",
                  }}
                />
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions data-testid="academic-assessment-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-academic-assessment-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !hasClasses || showEditSpinner}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-academic-assessment-button"
          >
            {isLoading
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
                ? "Save changes"
                : "Create assessment"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageAcademicAssessmentDialog;
